import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, Menu, Truck, ChevronDown } from "lucide-react";
import { useStore } from "../context/StoreContext";
import api from "../lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";

const CATEGORIES = [
  { slug: "gift-boxes", name: "Gift Boxes" },
  { slug: "dry-fruits", name: "Dry Fruits" },
  { slug: "nuts", name: "Nuts" },
  { slug: "seeds", name: "Seeds" },
  { slug: "berries", name: "Berries" },
  { slug: "exotic-collection", name: "Exotic Collection" },
  { slug: "corporate-gifts", name: "Corporate Gifts" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
    <div className="w-10 h-10 rounded-full bg-forest flex items-center justify-center border border-gold/40">
      <span className="font-serif text-gold text-xl leading-none">A</span>
    </div>
    <div className="leading-tight">
      <div className="font-serif text-xl tracking-tight text-forest">Aadvan</div>
      <div className="text-[9px] tracking-[0.3em] uppercase text-gold -mt-1">Dry Fruits</div>
    </div>
  </Link>
);

export const Navbar = () => {
  const { cartCount, wishlist, setCartOpen } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/products?search=${encodeURIComponent(search.trim())}`);
      setShowSearch(false);
      setMobileOpen(false);
    }
  };

  return (
    <>
      <div className="w-full bg-forest text-cream text-xs py-2 text-center tracking-wide flex items-center justify-center gap-2">
        <Truck size={14} className="text-gold" />
        <span>Same-day delivery within 15 KM of Sitapur • Cash on Delivery available</span>
      </div>
      <header
        className={`sticky top-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-cream border-b border-transparent"
        }`}
        data-testid="main-navbar"
      >
        <nav className="max-w-7xl mx-auto px-4 lg:px-8 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <Link to="/" className="hover:text-gold transition-colors" data-testid="nav-home">Home</Link>
              <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
                <button className="flex items-center gap-1 hover:text-gold transition-colors" data-testid="nav-categories">
                  Categories <ChevronDown size={14} />
                </button>
                {catOpen && (
                  <div className="absolute top-full left-0 pt-3">
                    <div className="bg-white border border-border rounded-lg shadow-xl p-2 w-56">
                      {CATEGORIES.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/category/${c.slug}`}
                          data-testid={`nav-cat-${c.slug}`}
                          className="block px-4 py-2 text-sm rounded-md hover:bg-sand hover:text-forest transition-colors"
                        >
                          {c.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Link to="/products" className="hover:text-gold transition-colors" data-testid="nav-products">Shop All</Link>
              <Link to="/about" className="hover:text-gold transition-colors" data-testid="nav-about">About</Link>
              <Link to="/track-order" className="hover:text-gold transition-colors" data-testid="nav-track">Track Order</Link>
              <Link to="/contact" className="hover:text-gold transition-colors" data-testid="nav-contact">Contact</Link>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={() => setShowSearch((s) => !s)} data-testid="nav-search-toggle" className="hover:text-gold transition-colors">
              <Search size={20} />
            </button>
            <Link to="/wishlist" className="relative hover:text-gold transition-colors" data-testid="nav-wishlist">
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-forest text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <button onClick={() => setCartOpen(true)} className="relative hover:text-gold transition-colors" data-testid="nav-cart-button">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-forest text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">
                  {cartCount}
                </span>
              )}
            </button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden hover:text-gold" data-testid="nav-mobile-toggle"><Menu size={22} /></button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream w-80">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <SheetDescription className="sr-only">Browse categories and pages.</SheetDescription>
                <div className="mt-8 flex flex-col gap-1">
                  <Link to="/" onClick={() => setMobileOpen(false)} className="py-3 border-b border-border">Home</Link>
                  <Link to="/products" onClick={() => setMobileOpen(false)} className="py-3 border-b border-border">Shop All</Link>
                  <div className="py-3 border-b border-border text-xs uppercase tracking-[0.2em] text-gold">Categories</div>
                  {CATEGORIES.map((c) => (
                    <Link key={c.slug} to={`/category/${c.slug}`} onClick={() => setMobileOpen(false)} className="py-2 pl-3 text-sm">
                      {c.name}
                    </Link>
                  ))}
                  <Link to="/about" onClick={() => setMobileOpen(false)} className="py-3 border-b border-t border-border mt-2">About</Link>
                  <Link to="/track-order" onClick={() => setMobileOpen(false)} className="py-3 border-b border-border">Track Order</Link>
                  <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-3 border-b border-border">Contact</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>

        {showSearch && (
          <div className="border-t border-border bg-white">
            <form onSubmit={doSearch} className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center gap-3">
              <Search size={18} className="text-muted-foreground" />
              <input
                autoFocus
                data-testid="nav-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search almonds, cashews, gift boxes…"
                className="flex-1 bg-transparent outline-none text-sm py-1"
              />
              <button type="submit" data-testid="nav-search-submit" className="text-xs uppercase tracking-widest text-gold">Search</button>
            </form>
          </div>
        )}
      </header>
    </>
  );
};
